import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { TipoContatoEnum } from '../../../../shared/enums/pessoa/tipo-contato.enum';
import { AbntPipe } from '../../../../shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { AuthService } from '../../../../shared/services/apis/usuario/auth.service';
import { MaskService } from '../../../../shared/services/mask.service';
import { PessoaLookupService } from '../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { RelatorioContasReceberService } from '../../../../shared/services/apis/financas/relatorio-contas-receber.service';
import { ListPessoaIdsRequest } from '../../../../shared/services/requests/pessoa/list-pessoa-ids-request';
import { RelatorioContasReceberRequest } from '../../../../shared/services/requests/financas/relatorio-contas-receber.request';
import { SaveRelatorioFiltroFavoritoRequest } from '../../../../shared/services/requests/financas/save-relatorio-filtro-favorito.request';
import { RelatorioFiltroFavorito } from '../../../../shared/services/responses/financas/relatorio-filtro-favorito.response';
import {
  CategoriaRelatorioItem,
  FormaRecebimentoItem,
  MeioRecebimentoItem,
  RelatorioContasReceberGrupo,
  RelatorioContasReceberItem,
} from '../../../../shared/services/responses/financas/relatorio-contas-receber.response';
import { FinancasLookupItem } from '../../../../shared/services/responses/financas/lookup-online-select.response';
import { environment } from '../../../../../environments/environment';
import { NomeFavoritoModalComponent } from '../relatorio-entradas-saidas-cliente/components/nome-favorito-modal/nome-favorito-modal.component';

type ModoPeriodo = 'mes' | 'intervalo';
type ClienteLookupItem = { id: number; descricao: string };

@Component({
  selector: 'app-relatorio-contas-receber',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    HeaderComponent,
    EmptyListComponent,
    LoadingBoxComponent,
    SelectPesquisavelComponent,
    AbntPipe,
  ],
  providers: [DatePipe],
  templateUrl: './relatorio-contas-receber.component.html',
  styleUrl: './relatorio-contas-receber.component.css',
})
export class RelatorioContasReceberComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Financas;
  public funcionalidadeEnum = FuncionalidadeEnum.Financas_Relatorios;

  public readonly periodoOpcoes = [
    { id: 'mes' as const, descricao: 'do mês' },
    { id: 'intervalo' as const, descricao: 'intervalo' },
  ];

  public situacaoOpcoes: FinancasLookupItem[] = [];

  public readonly agrupamentoOpcoes = [
    { id: 'nenhum', descricao: 'Não agrupar' },
    { id: 'cliente', descricao: 'Cliente' },
    { id: 'vencimento', descricao: 'Data de vencimento' },
    { id: 'categoria', descricao: 'Categoria' },
    { id: 'formaRecebimento', descricao: 'Forma de recebimento' },
  ];

  public readonly colunasOpcionaisOpcoes = [
    { id: 'taxas', descricao: 'Taxas' },
    { id: 'valorLiquido', descricao: 'Valor Líquido' },
    { id: 'idCliente', descricao: 'ID Cliente' },
    { id: 'cnpjCliente', descricao: 'CNPJ do Cliente' },
    { id: 'dataRecebimento', descricao: 'Data Recebimento' },
    { id: 'situacao', descricao: 'Situação' },
  ];

  public form = this.fb.group({
    modoPeriodo: ['mes' as ModoPeriodo],
    mesAno: [this.getMesAnoAtual()],
    dataInicial: [null as Date | null],
    dataFinal: [null as Date | null],
    situacoes: [[] as number[]],
    agrupamento: ['nenhum'],
    idCategoriaFinanceira: [null as number | null],
    idCliente: [null as number | null],
    idMeioRecebimento: [null as number | null],
    tipoMeioRecebimento: [null as string | null],
    idFormaRecebimento: [null as number | null],
    colunasVisiveis: [[] as string[]],
    favoritoSelecionado: [null as string | null],
  });

  public carregandoLookups = true;
  public carregandoRelatorio = false;
  public filtrosVisiveis = true;
  public consultaGerada = false;
  public possuiResultados = false;
  public mensagemEmptyState = '';
  public colunasPopoverAberto = false;
  public readonly urlCliente = `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=${TipoContatoEnum.Cliente}`;
  public clienteSelecionado: ClienteLookupItem | null = null;

  public categorias: CategoriaRelatorioItem[] = [];
  public meiosRecebimento: MeioRecebimentoItem[] = [];
  public formasRecebimento: FormaRecebimentoItem[] = [];
  public favoritos: RelatorioFiltroFavorito[] = [];
  public listaItens: RelatorioContasReceberItem[] = [];
  public listaGrupos: RelatorioContasReceberGrupo[] = [];
  public totalValor = 0;
  public totalSaldo = 0;
  public totalRecebido = 0;
  public agrupamentoAtual = 'nenhum';
  public colunasViseisAtual: string[] = [];

  private readonly codigoRelatorio = 'CONTAS_A_RECEBER';
  private readonly favoritosStorageKey = `financas-${this.codigoRelatorio.toLowerCase()}-favoritos`;
  private idTipoRelatorio: number | null = null;
  private periodoDescricaoGerado = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private alertService: AlertService,
    private relatorioService: RelatorioContasReceberService,
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService,
    private pessoaLookupService: PessoaLookupService
  ) {}

  public ngOnInit(): void {
    this.carregarLookups();
    this.configurarMudancaPeriodo();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public voltar(): void {
    this.router.navigate(['/financas/relatorios']);
  }

  public toggleFiltros(): void {
    this.filtrosVisiveis = !this.filtrosVisiveis;
  }

  public toggleColunasPopover(): void {
    this.colunasPopoverAberto = !this.colunasPopoverAberto;
  }

  public fecharColunasPopover(): void {
    this.colunasPopoverAberto = false;
  }

  // ─── Getters ───────────────────────────────────────────────────────────────

  public getIconoAgrupamento(): string {
    switch (this.agrupamentoAtual) {
      case 'vencimento':
        return 'event';
      case 'cliente':
        return 'person';
      case 'categoria':
        return 'category';
      case 'formaRecebimento':
        return 'payments';
      default:
        return 'view_agenda';
    }
  }

  public get isModoMes(): boolean {
    return this.form.get('modoPeriodo')?.value === 'mes';
  }

  public get mesAnoControl(): FormControl<string> {
    return this.form.get('mesAno') as FormControl<string>;
  }

  public get dataInicialControl(): FormControl<Date | null> {
    return this.form.get('dataInicial') as FormControl<Date | null>;
  }

  public get dataFinalControl(): FormControl<Date | null> {
    return this.form.get('dataFinal') as FormControl<Date | null>;
  }

  public get colunasVisiveisControl(): FormControl<string[]> {
    return this.form.get('colunasVisiveis') as FormControl<string[]>;
  }

  public get hasFavoritoSelecionado(): boolean {
    return !!this.favoritoSelecionadoAtual;
  }

  public get hasAlgumFiltroPreenchido(): boolean {
    const situacoes = this.form.get('situacoes')?.value ?? [];
    const agrupamento = this.form.get('agrupamento')?.value ?? 'nenhum';
    const idCategoria = this.form.get('idCategoriaFinanceira')?.value ?? null;
    const idCliente = this.form.get('idCliente')?.value ?? null;
    const idMeio = this.form.get('idMeioRecebimento')?.value ?? null;
    const idForma = this.form.get('idFormaRecebimento')?.value ?? null;
    const colunas = this.colunasVisiveisControl.value ?? [];
    if (
      situacoes.length ||
      agrupamento !== 'nenhum' ||
      idCategoria ||
      idCliente ||
      idMeio ||
      idForma ||
      colunas.length
    )
      return true;
    if (!this.isModoMes) {
      return !!(this.dataInicialControl.value || this.dataFinalControl.value);
    }
    return false;
  }

  public get favoritoSelecionadoAtual(): RelatorioFiltroFavorito | null {
    const id = this.form.get('favoritoSelecionado')?.value;
    return this.favoritos.find(f => String(f.idRelatorioFiltroFavorito) === id) || null;
  }

  public get colunasVisiveisDescricao(): string {
    const selecionadas = this.colunasVisiveisControl.value ?? [];
    if (!selecionadas.length) return 'Sem informações visíveis';
    return selecionadas
      .map(id => this.colunasOpcionaisOpcoes.find(o => o.id === id)?.descricao ?? id)
      .join(', ');
  }

  // ─── Column visibility ─────────────────────────────────────────────────────

  public isColunaVisivel(id: string): boolean {
    return (this.colunasVisiveisControl.value ?? []).includes(id);
  }

  public isColunaVisivelAtual(id: string): boolean {
    return this.colunasViseisAtual.includes(id);
  }

  public toggleColuna(id: string): void {
    const current = [...(this.colunasVisiveisControl.value ?? [])];
    const idx = current.indexOf(id);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(id);
    this.colunasVisiveisControl.setValue(current);
  }

  public selecionarTodasColunas(): void {
    const todas = this.colunasOpcionaisOpcoes.map(o => o.id);
    const current = this.colunasVisiveisControl.value ?? [];
    const allSelected = todas.every(id => current.includes(id));
    this.colunasVisiveisControl.setValue(allSelected ? [] : todas);
  }

  public get todasColunasSelected(): boolean {
    const todas = this.colunasOpcionaisOpcoes.map(o => o.id);
    const current = this.colunasVisiveisControl.value ?? [];
    return todas.every(id => current.includes(id));
  }

  public get algumaColunaSelecionada(): boolean {
    return (this.colunasVisiveisControl.value ?? []).length > 0;
  }

  // ─── Categoria helpers ─────────────────────────────────────────────────────

  public getCategoriaLabel(item: CategoriaRelatorioItem): string {
    return '\u00A0\u00A0'.repeat(item.nivel) + item.descricao;
  }

  // ─── Period ────────────────────────────────────────────────────────────────

  public selecionarModoPeriodo(modo: ModoPeriodo): void {
    this.form.patchValue({ modoPeriodo: modo });
  }

  public onDateInput(event: Event, controlName: 'dataInicial' | 'dataFinal'): void {
    MaskService.onDateInput(event, this.form.get(controlName));
  }

  public navegarMes(passos: number): void {
    const ref = this.parseMesAno(this.mesAnoControl.value) ?? new Date();
    ref.setMonth(ref.getMonth() + passos);
    this.mesAnoControl.setValue(this.formatMesAno(ref));
  }

  public normalizarMesAno(): void {
    const norm = this.parseMesAno(this.mesAnoControl.value);
    this.mesAnoControl.setValue(norm ? this.formatMesAno(norm) : this.getMesAnoAtual());
  }

  // ─── Favorites ─────────────────────────────────────────────────────────────

  public favoritarFiltros(): void {
    if (this.hasFavoritoSelecionado) {
      this.removerFavoritoSelecionado();
      return;
    }

    const dialogRef = this.dialog.open(NomeFavoritoModalComponent, {
      width: '480px',
      maxWidth: '95vw',
      autoFocus: false,
      data: { nomeInicial: this.getProximoNomeFavorito() },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((nome: string | undefined) => {
        if (!nome?.trim()) return;
        this.salvarFavorito(nome.trim());
      });
  }

  public carregarFavoritoSelecionado(): void {
    const id = this.form.get('favoritoSelecionado')?.value;
    if (!id) {
      this.limparFiltrosFavorito();
      return;
    }

    const favorito = this.favoritos.find(f => String(f.idRelatorioFiltroFavorito) === id);
    if (!favorito) {
      this.limparFiltrosFavorito();
      return;
    }

    this.form.patchValue({
      modoPeriodo: (favorito.filtro.modoPeriodo === 'intervalo' ? 'intervalo' : 'mes') as ModoPeriodo,
      mesAno: favorito.filtro.mesAno ?? this.getMesAnoAtual(),
      dataInicial: favorito.filtro.dataInicial ? new Date(favorito.filtro.dataInicial) : null,
      dataFinal: favorito.filtro.dataFinal ? new Date(favorito.filtro.dataFinal) : null,
      situacoes: favorito.filtro.situacoes ?? [],
      idCategoriaFinanceira: favorito.filtro.idCategoriaFinanceira ?? null,
      idCliente: favorito.filtro.idCliente ?? null,
    });
    this.clienteSelecionado = favorito.filtro.idCliente
      ? { id: favorito.filtro.idCliente, descricao: favorito.filtro.clienteDescricao ?? '' }
      : null;
  }

  public getFavoritoValue(favorito: RelatorioFiltroFavorito): string {
    return String(favorito.idRelatorioFiltroFavorito);
  }

  public onClienteSelecionado(cliente: ClienteLookupItem | null): void {
    this.clienteSelecionado = cliente && cliente.id ? cliente : null;
  }

  // ─── Lookup ────────────────────────────────────────────────────────────────

  public carregarLookups(): void {
    this.carregandoLookups = true;
    this.relatorioService
      .getLookup()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.categorias = response.listaCategorias || [];
          this.situacaoOpcoes = response.listaSituacoes || [];
          this.meiosRecebimento = response.listaMeiosRecebimento || [];
          this.formasRecebimento = response.listaFormasRecebimento || [];
          this.idTipoRelatorio = response.idTipoRelatorio ?? null;
          this.favoritos = response.listaFavoritos || [];
          if (!this.idTipoRelatorio) this.carregarFavoritosLocalStorage();
          this.carregandoLookups = false;
        },
        error: () => {
          this.carregandoLookups = false;
          this.alertService.show('Erro', 'Erro ao carregar os filtros do relatório.', AlertType.error);
        },
      });
  }

  // ─── Generate ──────────────────────────────────────────────────────────────

  public gerar(): void {
    if (!this.validarPeriodo()) return;

    const { inicio, fim } = this.getPeriodoSelecionado();
    if (!inicio || !fim) {
      this.alertService.show(
        'Atenção',
        'Informe um período válido para gerar o relatório.',
        AlertType.warning
      );
      return;
    }

    this.periodoDescricaoGerado = this.getPeriodoDescricao();
    this.consultaGerada = true;
    this.carregandoRelatorio = true;
    this.mensagemEmptyState = '';

    const meioId = this.form.get('idMeioRecebimento')?.value ?? null;
    const meioTipo = this.form.get('tipoMeioRecebimento')?.value ?? null;

    const request: RelatorioContasReceberRequest = {
      dataInicio: this.datePipe.transform(inicio, 'yyyy-MM-dd') || '',
      dataFim: this.datePipe.transform(fim, 'yyyy-MM-dd') || '',
      situacoes: this.form.get('situacoes')?.value ?? [],
      agrupamento: this.form.get('agrupamento')?.value || 'nenhum',
      idCategoriaFinanceira: this.form.get('idCategoriaFinanceira')?.value ?? null,
      idCliente: this.form.get('idCliente')?.value ?? null,
      nomeCliente: this.clienteSelecionado?.descricao?.trim() || null,
      idContaFinanceira: meioTipo === 'conta' ? meioId : null,
      idIntermediadorPagamento: meioTipo === 'intermediador' ? meioId : null,
      idFormaRecebimento: this.form.get('idFormaRecebimento')?.value ?? null,
      colunasVisiveis: this.colunasVisiveisControl.value ?? [],
    };

    this.relatorioService
      .getPreview(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.agrupamentoAtual = response.agrupamento || 'nenhum';
          this.colunasViseisAtual = [...(request.colunasVisiveis ?? [])];
          this.listaItens = (response.listaItens || []).map(i => ({
            ...i,
            valor: Number(i.valor || 0),
            saldo: Number(i.saldo || 0),
            recebido: Number(i.recebido || 0),
            taxas: i.taxas != null ? Number(i.taxas) : null,
            valorLiquido: i.valorLiquido != null ? Number(i.valorLiquido) : null,
          }));
          this.listaGrupos = (response.listaGrupos || []).map(g => ({
            ...g,
            subtotalValor: Number(g.subtotalValor || 0),
            subtotalSaldo: Number(g.subtotalSaldo || 0),
            subtotalRecebido: Number(g.subtotalRecebido || 0),
            itens: (g.itens || []).map(i => ({
              ...i,
              valor: Number(i.valor || 0),
              saldo: Number(i.saldo || 0),
              recebido: Number(i.recebido || 0),
              taxas: i.taxas != null ? Number(i.taxas) : null,
              valorLiquido: i.valorLiquido != null ? Number(i.valorLiquido) : null,
            })),
          }));
          this.totalValor = Number(response.totalValor || 0);
          this.totalSaldo = Number(response.totalSaldo || 0);
          this.totalRecebido = Number(response.totalRecebido || 0);
          this.aplicarNomesClientes();
        },
        error: () => {
          this.listaItens = [];
          this.listaGrupos = [];
          this.totalValor = 0;
          this.totalSaldo = 0;
          this.totalRecebido = 0;
          this.possuiResultados = false;
          this.filtrosVisiveis = true;
          this.carregandoRelatorio = false;
          this.alertService.show('Erro', 'Erro ao gerar o relatório.', AlertType.error);
        },
      });
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  public imprimir(): void {
    if (!this.possuiResultados) return;

    const esc = (v: string | null | undefined) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const periodo = this.periodoDescricaoGerado;
    const geradoEm = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') || '';
    const empresa = this.authService.getUserInfos().nomeEmpresa;
    const colunas = this.colunasViseisAtual;

    const buildOptCols = (item: RelatorioContasReceberItem) =>
      [
        colunas.includes('taxas')
          ? `<td class="valor">${item.taxas != null ? this.formatarMoeda(item.taxas) : '—'}</td>`
          : '',
        colunas.includes('valorLiquido')
          ? `<td class="valor">${item.valorLiquido != null ? this.formatarMoeda(item.valorLiquido) : '—'}</td>`
          : '',
        colunas.includes('idCliente') ? `<td>${esc(String(item.idPessoaCliente || ''))}</td>` : '',
        colunas.includes('cnpjCliente') ? `<td>${esc(item.cnpjCliente)}</td>` : '',
        colunas.includes('dataRecebimento') ? `<td>${this.formatarData(item.dataRecebimento)}</td>` : '',
        colunas.includes('situacao') ? `<td>${esc(item.situacao)}</td>` : '',
      ].join('');

    const optHeaders = [
      colunas.includes('taxas') ? '<th class="valor">Taxas</th>' : '',
      colunas.includes('valorLiquido') ? '<th class="valor">Vlr. Líquido</th>' : '',
      colunas.includes('idCliente') ? '<th>ID Cliente</th>' : '',
      colunas.includes('cnpjCliente') ? '<th>CNPJ</th>' : '',
      colunas.includes('dataRecebimento') ? '<th>Dt. Recebimento</th>' : '',
      colunas.includes('situacao') ? '<th>Situação</th>' : '',
    ].join('');

    const thead = `<thead><tr>
            <th>Cliente</th><th>Descrição</th><th>Categoria</th>
            <th>Nº Banco</th><th>Nº Doc.</th><th>Emissão</th><th>Vencimento</th>
            <th class="valor">Valor</th><th class="valor">Saldo</th><th class="valor">Recebido</th>
            ${optHeaders}
        </tr></thead>`;

    const buildRows = (itens: RelatorioContasReceberItem[]) =>
      itens
        .map(
          item => `
            <tr${item.isAtrasada ? ' class="atrasada"' : ''}${item.isAntecipada ? ' class="antecipada"' : ''}>
                <td>${esc(item.cliente)}</td>
                <td>${esc(item.historico)}</td>
                <td>${esc(item.categoria)}</td>
                <td>${esc(item.nroBanco)}</td>
                <td>${esc(item.nroDocumento)}</td>
                <td>${this.formatarData(item.dataEmissao)}</td>
                <td>${this.formatarData(item.dataVencimento)}</td>
                <td class="valor">${this.formatarMoeda(item.valor)}</td>
                <td class="valor">${this.formatarMoeda(item.saldo)}</td>
                <td class="valor">${this.formatarMoeda(item.recebido)}</td>
                ${buildOptCols(item)}
            </tr>`
        )
        .join('');

    let tablesHtml = '';
    if (this.agrupamentoAtual === 'nenhum') {
      tablesHtml = `<table>${thead}<tbody>${buildRows(this.listaItens)}</tbody></table>`;
    } else {
      tablesHtml = this.listaGrupos
        .map(
          g => `
                <section class="grupo">
                    <h2>${esc(g.descricaoGrupo)}</h2>
                    <table>${thead}<tbody>${buildRows(g.itens)}</tbody></table>
                    <div class="subtotal">Subtotal — Valor: ${this.formatarMoeda(g.subtotalValor)} | Saldo: ${this.formatarMoeda(g.subtotalSaldo)} | Recebido: ${this.formatarMoeda(g.subtotalRecebido)}</div>
                </section>`
        )
        .join('');
    }

    const conteudo = `<html>
        <head>
            <title>Relatório de Contas a Receber</title>
            <style>
                body { font-family: Arial, sans-serif; color: #1f2937; padding: 24px; font-size: 12px; }
                h1 { margin: 0 0 8px; font-size: 20px; }
                .meta { margin-bottom: 20px; color: #475569; font-size: 11px; }
                .grupo { margin-bottom: 24px; page-break-inside: avoid; }
                .grupo h2 { font-size: 14px; margin: 0 0 8px; color: #374151; }
                .subtotal { font-size: 11px; color: #374151; margin-top: 4px; text-align: right; font-weight: 600; }
                table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
                thead { display: table-header-group; }
                th, td { border-bottom: 1px solid #e5e7eb; padding: 7px 6px; text-align: left; }
                th.valor, td.valor { text-align: right; }
                .atrasada td { color: #b73232; }
                .antecipada td { font-style: italic; }
                @media print { @page { margin: 1.5cm; } }
            </style>
        </head>
        <body>
            <h1>Relatório de Contas a Receber</h1>
            <div class="meta">
                <div>Empresa: ${empresa}</div>
                <div>Período: ${periodo}</div>
                <div>Gerado em: ${geradoEm}</div>
            </div>
            ${tablesHtml}
            <div style="margin-top:16px;font-weight:700;text-align:right;">
                Total Valor: ${this.formatarMoeda(this.totalValor)} &nbsp;|&nbsp;
                Total Saldo: ${this.formatarMoeda(this.totalSaldo)} &nbsp;|&nbsp;
                Total Recebido: ${this.formatarMoeda(this.totalRecebido)}
            </div>
        </body>
        </html>`;

    const popup = window.open('', '_blank', 'width=1200,height=900');
    if (!popup) return;
    popup.document.write(conteudo);
    popup.document.close();
    popup.focus();
    popup.print();
  }

  public downloadCsv(): void {
    if (!this.possuiResultados) return;

    const sep = ';';
    const colunas = this.colunasViseisAtual;
    // pt-BR (vírgula decimal) — consistente com o separador ';' do CSV; .toFixed() usaria ponto e o
    // Excel-BR trataria a coluna como texto, quebrando fórmulas. Reaproveita o AbntPipe já usado no app
    // (mesmo arredondamento ABNT do resto do sistema).
    const abntPipe = new AbntPipe(this.authService);
    const formatarNumero = (valor: number) => abntPipe.transform(valor, 'quantidade', 2);

    const baseHeaders = [
      'Vencimento',
      'Cliente',
      'Descrição',
      'Nº Banco',
      'Nº Documento',
      'Data de Emissão',
      'Valor (R$)',
      'Saldo (R$)',
      'Recebido (R$)',
      'Antecipada',
    ];
    const optHeaders = [
      colunas.includes('taxas') ? 'Taxas (R$)' : null,
      colunas.includes('valorLiquido') ? 'Valor Líquido (R$)' : null,
      colunas.includes('idCliente') ? 'ID Cliente' : null,
      colunas.includes('cnpjCliente') ? 'CNPJ do Cliente' : null,
      colunas.includes('dataRecebimento') ? 'Data Recebimento' : null,
      colunas.includes('situacao') ? 'Situação' : null,
    ].filter(Boolean) as string[];

    const header = [...baseHeaders, ...optHeaders].join(sep);

    const toRow = (item: RelatorioContasReceberItem) => {
      const base = [
        this.formatarData(item.dataVencimento),
        item.cliente,
        item.historico,
        item.nroBanco ?? '',
        item.nroDocumento ?? '',
        this.formatarData(item.dataEmissao),
        formatarNumero(item.valor),
        formatarNumero(item.saldo),
        formatarNumero(item.recebido),
        item.isAntecipada ? 'Sim' : 'Não',
      ];
      const opt = [
        colunas.includes('taxas') ? (item.taxas != null ? formatarNumero(item.taxas) : '') : null,
        colunas.includes('valorLiquido')
          ? item.valorLiquido != null
            ? formatarNumero(item.valorLiquido)
            : ''
          : null,
        colunas.includes('idCliente') ? String(item.idPessoaCliente || '') : null,
        colunas.includes('cnpjCliente') ? (item.cnpjCliente ?? '') : null,
        colunas.includes('dataRecebimento') ? this.formatarData(item.dataRecebimento) : null,
        colunas.includes('situacao') ? (item.situacao ?? '') : null,
      ].filter(v => v !== null) as string[];
      return [...base, ...opt].join(sep);
    };

    const itens =
      this.agrupamentoAtual === 'nenhum' ? this.listaItens : this.listaGrupos.flatMap(g => g.itens);

    const csv = '\uFEFF' + [header, ...itens.map(toRow)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'relatorio-contas-a-receber.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  // ─── Resolução de nome real do Cliente ──────────────────────────────────────
  // O backend expõe "cliente" como descricaoOriginal do documento (texto livre do lançamento), não o nome
  // cadastral da Pessoa — mesma causa-raiz já corrigida em impressao-contas-pagar-dialog.component.ts.
  // Resolve aqui via PessoaLookupService, sobrescrevendo tanto o texto de cada linha (listaItens e dentro
  // de cada grupo) quanto o cabeçalho de cada grupo quando o agrupamento é por Cliente.

  private aplicarNomesClientes(): void {
    const idsClientes = Array.from(
      new Set([
        ...this.listaItens.map(i => i.idPessoaCliente),
        ...this.listaGrupos.flatMap(g => g.itens.map(i => i.idPessoaCliente)),
        ...this.listaGrupos.map(g => g.idPessoaCliente ?? 0),
      ].filter(id => !!id && id > 0))
    );

    const finalizar = (nomesPorId: Map<number, string>): void => {
      const resolverNome = (idPessoaCliente: number, textoAtual: string): string =>
        idPessoaCliente > 0 ? nomesPorId.get(idPessoaCliente) || textoAtual : textoAtual;

      this.listaItens = this.listaItens.map(item => ({
        ...item,
        cliente: resolverNome(item.idPessoaCliente, item.cliente),
      }));

      this.listaGrupos = this.listaGrupos.map(grupo => ({
        ...grupo,
        descricaoGrupo:
          grupo.idPessoaCliente != null
            ? resolverNome(grupo.idPessoaCliente, grupo.descricaoGrupo)
            : grupo.descricaoGrupo,
        itens: grupo.itens.map(item => ({
          ...item,
          cliente: resolverNome(item.idPessoaCliente, item.cliente),
        })),
      }));

      this.possuiResultados = this.listaItens.length > 0 || this.listaGrupos.length > 0;
      this.filtrosVisiveis = !this.possuiResultados;
      this.mensagemEmptyState = this.possuiResultados
        ? ''
        : 'A consulta não retornou nenhum registro. Tente refazer a consulta com outros parâmetros.';
      this.carregandoRelatorio = false;
    };

    if (!idsClientes.length) {
      finalizar(new Map());
      return;
    }

    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = idsClientes;

    this.pessoaLookupService
      .getListPessoaIds(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError) {
            finalizar(new Map());
            return;
          }
          const nomesPorId = new Map(
            (response.listaPessoa || [])
              .filter(item => !!item.idPessoa && !!item.nome?.trim())
              .map(item => [item.idPessoa, item.nome.trim()])
          );
          finalizar(nomesPorId);
        },
        error: () => finalizar(new Map()),
      });
  }

  // ─── Formatting ────────────────────────────────────────────────────────────

  public formatarData(value: string | Date | null | undefined): string {
    if (!value) return '—';
    const normalizado = typeof value === 'string' && !value.includes('T') ? `${value}T00:00:00` : value;
    return this.datePipe.transform(normalizado, 'dd/MM/yyyy') || '—';
  }

  public formatarMoeda(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  public getPeriodoDescricao(): string {
    if (this.isModoMes) return this.mesAnoControl.value;
    const inicio = this.dataInicialControl.value
      ? this.datePipe.transform(this.dataInicialControl.value, 'dd/MM/yyyy')
      : '';
    const fim = this.dataFinalControl.value
      ? this.datePipe.transform(this.dataFinalControl.value, 'dd/MM/yyyy')
      : '';
    return `${inicio} a ${fim}`;
  }

  // ─── Meio recebimento helpers ──────────────────────────────────────────────

  public selecionarMeioRecebimento(meio: MeioRecebimentoItem | null): void {
    this.form.patchValue({
      idMeioRecebimento: meio?.id ?? null,
      tipoMeioRecebimento: meio?.tipo ?? null,
    });
  }

  public getMeioRecebimentoSelecionado(): MeioRecebimentoItem | null {
    const id = this.form.get('idMeioRecebimento')?.value;
    const tipo = this.form.get('tipoMeioRecebimento')?.value;
    return this.meiosRecebimento.find(m => m.id === id && m.tipo === tipo) ?? null;
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  private configurarMudancaPeriodo(): void {
    this.form
      .get('modoPeriodo')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(modo => {
        if (!modo) return;
        if (modo === 'mes') {
          this.dataInicialControl.setValue(null);
          this.dataFinalControl.setValue(null);
        } else {
          this.mesAnoControl.setValue(this.getMesAnoAtual());
        }
      });
  }

  private validarPeriodo(): boolean {
    if (this.isModoMes) {
      if (!this.parseMesAno(this.mesAnoControl.value)) {
        this.alertService.show('Atenção', 'Informe um mês/ano válido no formato MM/AAAA.', AlertType.warning);
        return false;
      }
      return true;
    }
    const ini = this.dataInicialControl.value;
    const fim = this.dataFinalControl.value;
    if (!ini || !fim) {
      this.alertService.show('Atenção', 'Informe a data inicial e a data final.', AlertType.warning);
      return false;
    }
    if (fim < ini) {
      this.alertService.show(
        'Atenção',
        'A data final não pode ser anterior à data inicial.',
        AlertType.warning
      );
      return false;
    }
    return true;
  }

  private getPeriodoSelecionado(): { inicio: Date | null; fim: Date | null } {
    if (this.isModoMes) {
      const ref = this.parseMesAno(this.mesAnoControl.value);
      if (!ref) return { inicio: null, fim: null };
      const inicio = new Date(ref.getFullYear(), ref.getMonth(), 1);
      const fim = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
      fim.setHours(23, 59, 59, 999);
      return { inicio, fim };
    }
    const inicio = this.dataInicialControl.value ? new Date(this.dataInicialControl.value) : null;
    const fim = this.dataFinalControl.value ? new Date(this.dataFinalControl.value) : null;
    if (fim) fim.setHours(23, 59, 59, 999);
    return { inicio, fim };
  }

  private limparFiltrosFavorito(): void {
    this.form.patchValue({
      favoritoSelecionado: null,
      modoPeriodo: 'mes',
      mesAno: this.getMesAnoAtual(),
      dataInicial: null,
      dataFinal: null,
      situacoes: [],
      agrupamento: 'nenhum',
      idCategoriaFinanceira: null,
      idCliente: null,
      idMeioRecebimento: null,
      tipoMeioRecebimento: null,
      idFormaRecebimento: null,
      colunasVisiveis: [],
    });
    this.clienteSelecionado = null;
  }

  private carregarFavoritosLocalStorage(): void {
    try {
      const raw = localStorage.getItem(this.favoritosStorageKey);
      this.favoritos = raw ? (JSON.parse(raw) as RelatorioFiltroFavorito[]) : [];
    } catch {
      this.favoritos = [];
    }
  }

  private persistirFavoritos(): void {
    if (this.idTipoRelatorio) return;
    localStorage.setItem(this.favoritosStorageKey, JSON.stringify(this.favoritos));
  }

  private getProximoNomeFavorito(): string {
    const numeros = this.favoritos
      .map(f => /^Favorito (\d+)$/.exec(f.nome)?.[1])
      .filter((v): v is string => !!v)
      .map(Number)
      .filter(v => !Number.isNaN(v));
    const next = numeros.length ? Math.max(...numeros) + 1 : this.favoritos.length + 1;
    return `Favorito ${next}`;
  }

  private salvarFavorito(nome: string): void {
    const filtro = {
      modoPeriodo: this.form.get('modoPeriodo')?.value || 'mes',
      mesAno: this.mesAnoControl.value,
      dataInicial: this.dataInicialControl.value
        ? this.datePipe.transform(this.dataInicialControl.value, 'yyyy-MM-dd')
        : null,
      dataFinal: this.dataFinalControl.value
        ? this.datePipe.transform(this.dataFinalControl.value, 'yyyy-MM-dd')
        : null,
      situacoes: this.form.get('situacoes')?.value ?? [],
      idCliente: this.form.get('idCliente')?.value ?? null,
      clienteDescricao: this.clienteSelecionado?.descricao?.trim() || null,
      idContaFinanceira:
        this.form.get('tipoMeioRecebimento')?.value === 'conta'
          ? (this.form.get('idMeioRecebimento')?.value ?? null)
          : null,
      idCategoriaFinanceira: this.form.get('idCategoriaFinanceira')?.value ?? null,
    };

    if (!this.idTipoRelatorio) {
      const favorito = {
        idRelatorioFiltroFavorito: Date.now(),
        idTipoRelatorio: 0,
        nome,
        isPadrao: false,
        datetimeInclusao: new Date().toISOString(),
        filtro,
      } as RelatorioFiltroFavorito;
      this.favoritos = [favorito, ...this.favoritos];
      this.persistirFavoritos();
      this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
      return;
    }

    const request: SaveRelatorioFiltroFavoritoRequest = {
      idTipoRelatorio: this.idTipoRelatorio,
      nome,
      isPadrao: false,
      filtro,
    };
    this.relatorioService
      .saveFavorito(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError || !response.favorito) {
            this.alertService.show(
              'Erro',
              response.errorDescription || 'Erro ao salvar favorito.',
              AlertType.error
            );
            return;
          }
          const favorito = response.favorito;
          this.favoritos = [
            favorito,
            ...this.favoritos.filter(f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito),
          ].sort((a, b) => a.nome.localeCompare(b.nome));
          this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
        },
        error: () => this.alertService.show('Erro', 'Erro ao salvar favorito.', AlertType.error),
      });
  }

  private removerFavoritoSelecionado(): void {
    const favorito = this.favoritoSelecionadoAtual;
    if (!favorito) return;

    if (!this.idTipoRelatorio) {
      this.favoritos = this.favoritos.filter(
        f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito
      );
      this.persistirFavoritos();
      this.form.patchValue({ favoritoSelecionado: null });
      return;
    }

    this.relatorioService
      .deleteFavorito(favorito.idRelatorioFiltroFavorito)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError) {
            this.alertService.show(
              'Erro',
              response.errorDescription || 'Erro ao remover favorito.',
              AlertType.error
            );
            return;
          }
          this.favoritos = this.favoritos.filter(
            f => f.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito
          );
          this.form.patchValue({ favoritoSelecionado: null });
        },
        error: () => this.alertService.show('Erro', 'Erro ao remover favorito.', AlertType.error),
      });
  }

  private getMesAnoAtual(): string {
    return this.formatMesAno(new Date());
  }

  private formatMesAno(date: Date): string {
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  private parseMesAno(value: string | null | undefined): Date | null {
    const match = (value || '').trim().match(/^(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const mes = Number(match[1]);
    const ano = Number(match[2]);
    if (mes < 1 || mes > 12) return null;
    return new Date(ano, mes - 1, 1);
  }
}
